import React from 'react';
import { Box, Paper, Typography, Stack, TextField, MenuItem, InputLabel, FormControl, Select, Chip, Button, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, LinearProgress, Divider, Alert, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Pagination } from '@mui/material';
import { fetchTemplates, type TemplateSummary } from '../api/templates';
import { excelMappingApi } from '../api/excelMapping';
import { useTranslation } from 'react-i18next';
import { Visibility, Download, Upload } from '@mui/icons-material';
import axiosClient from '../api/axiosClient';

const TemplatesPage: React.FC = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = React.useState<boolean>(false);
  const [rows, setRows] = React.useState<TemplateSummary[]>([]);
  const [paginationModel, setPaginationModel] = React.useState<{ page: number; pageSize: number }>({ page: 0, pageSize: 10 });
  const [rowCount, setRowCount] = React.useState<number>(0);
  const [search, setSearch] = React.useState<string>('');
  const [statusFilter, setStatusFilter] = React.useState<string>('');
  const [categoryFilter, setCategoryFilter] = React.useState<string>('');
  const [reportTypes, setReportTypes] = React.useState<string[]>([]);
  const searchRef = React.useRef<number | undefined>(undefined);
  
  // Modal de detalle
  const [detailOpen, setDetailOpen] = React.useState<boolean>(false);
  const [selectedTemplate, setSelectedTemplate] = React.useState<any>(null);
  const [detailLoading, setDetailLoading] = React.useState<boolean>(false);
  const [uploadFile, setUploadFile] = React.useState<File | null>(null);
  const [uploading, setUploading] = React.useState<boolean>(false);
  const [dragActive, setDragActive] = React.useState<boolean>(false);
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);

  const formatVariables = (variables: string[] | undefined): string => {
    if (!Array.isArray(variables) || variables.length === 0) return '';
    const joined = variables.join(', ');
    return joined.length > 50 ? joined.substring(0, 50) + '...' : joined;
  };

  const formatDate = (dateStr: string | undefined): string => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
    } catch {
      return dateStr;
    }
  };

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, any> = {
        page: paginationModel.page + 1,
        limit: paginationModel.pageSize,
      };
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      if (categoryFilter) params.type = categoryFilter;
      const res = await fetchTemplates(params);
      const mappedRows = (res.data || []).map((r, idx) => ({
        ...r,
        id: r.id ?? String(idx),
      }));
      setRows(mappedRows);
      setRowCount((res.total ?? (Array.isArray(res.data) ? res.data.length : 0)) || 0);
    } catch (e) {
      // noop UI minimal
    } finally {
      setLoading(false);
    }
  }, [paginationModel.page, paginationModel.pageSize, search, statusFilter, categoryFilter]);

  React.useEffect(() => { load(); }, [load]);

  // Cargar tipos de informe para el filtro
  React.useEffect(() => {
    (async () => {
      try {
        const resp = await excelMappingApi.getReportTypes();
        const data = resp?.data?.data || resp?.data || [];
        const types = Array.isArray((data as any).available_types) ? (data as any).available_types : Array.isArray(data) ? data : [];
        setReportTypes(types as string[]);
      } catch {}
    })();
  }, []);

  const loadTemplateDetail = async (templateId: string) => {
    setDetailLoading(true);
    try {
      const { getActiveConfigProfile } = await import('../api/configProfiles');
      const prof = await getActiveConfigProfile();
      const apinformesUrl: string | undefined = prof?.apinformesUrl || undefined;
      if (apinformesUrl) {
        const base = apinformesUrl.replace(/\/$/, '');
        const response = await fetch(`${base}/api/templates/${templateId}`);
        const data = await response.json();
        setSelectedTemplate(data?.data || data);
      }
    } catch (e) {
      console.error('Error loading template detail:', e);
    } finally {
      setDetailLoading(false);
    }
  };

        const downloadTemplate = async () => {
          if (!selectedTemplate?.id) return;
          try {
            const resp = await axiosClient.get(`/reports/templates/download/${selectedTemplate.id}`, { responseType: 'blob' as any });
            const blob = new Blob([resp.data]);
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = selectedTemplate.original_filename || `${selectedTemplate.name}.docx`;
            a.click();
            URL.revokeObjectURL(url);
          } catch (e) {
            console.error('Error downloading template:', e);
          }
        };

  const uploadTemplate = async () => {
    if (!uploadFile || !selectedTemplate) return;
    setUploading(true);
    try {
      const { getActiveConfigProfile } = await import('../api/configProfiles');
      const prof = await getActiveConfigProfile();
      const apinformesUrl: string | undefined = prof?.apinformesUrl || undefined;
      if (apinformesUrl) {
        const base = apinformesUrl.replace(/\/$/, '');
        const formData = new FormData();
        formData.append('template_file', uploadFile);
        formData.append('name', selectedTemplate.name);
        formData.append('description', selectedTemplate.description || '');
        formData.append('category', selectedTemplate.category || '');
        // Mantener variables con corchetes, ej.: [nif]
        formData.append('syntax_type', 'square_brackets');
        
        const response = await fetch(`${base}/template-generator/upload-raw`, {
          method: 'POST',
          body: formData,
        });
        const result = await response.json();
        if (result.success) {
          setUploadFile(null);
          setDetailOpen(false);
          load(); // Recargar lista
        }
      }
    } catch (e) {
      console.error('Error uploading template:', e);
    } finally {
      setUploading(false);
    }
  };

  // Tabla (MUI Table) utiliza renderizado manual de filas/columnas

  const clearFilters = () => {
    setSearch('');
    setStatusFilter('');
    setCategoryFilter('');
    setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
  };

  const exportCsv = () => {
    const headers = ['name','description','category','status','sizeKb','variables','updatedAt'];
    const lines = [headers.join(',')].concat(
      rows.map(r => [
        r.name ?? '',
        (r.description ?? '').replaceAll('"', '""'),
        r.category ?? '',
        r.status ?? '',
        String(r.sizeKb ?? ''),
        formatVariables(r.variables),
        formatDate(r.updatedAt)
      ].map(v => /[",\n]/.test(String(v)) ? `"${String(v)}"` : String(v)).join(','))
    );
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'templates.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const activeChips = (
    <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
      {search && <Chip label={`${t('common.search')}: ${search}`} onDelete={() => setSearch('')} />}
      {statusFilter && <Chip label={`${t('common.status')}: ${statusFilter}`} onDelete={() => setStatusFilter('')} />}
      {categoryFilter && <Chip label={`${t('templates.reportType', 'Tipo de informe')}: ${categoryFilter}`} onDelete={() => setCategoryFilter('')} />}
    </Stack>
  );

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
        <Typography variant="h5" fontWeight={700}>{t('templates.title')}</Typography>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" onClick={clearFilters}>{t('common.filter', 'Filtro')} {t('common.clear', 'Limpiar')}</Button>
          <Button variant="contained" onClick={exportCsv}>{t('exportCsv.downloadCsv')}</Button>
        </Stack>
      </Stack>

      {/* Filtros */}
      <Paper sx={{ mb: 1, p: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
          <TextField
            label={t('common.search')}
            value={search}
            onChange={(e) => {
              const val = e.target.value;
              setSearch(val);
              if (searchRef.current) window.clearTimeout(searchRef.current);
              searchRef.current = window.setTimeout(() => setPaginationModel((m) => ({ ...m, page: 0 })), 300);
            }}
            fullWidth
          />

          <FormControl sx={{ minWidth: 160 }}>
            <InputLabel>{t('common.status')}</InputLabel>
            <Select
              label={t('common.status')}
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value as string); setPaginationModel((m) => ({ ...m, page: 0 })); }}
            >
              <MenuItem value="">{t('common.all')}</MenuItem>
              <MenuItem value="active">{t('common.active')}</MenuItem>
              <MenuItem value="inactive">{t('common.inactive')}</MenuItem>
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>{t('templates.reportType', 'Tipo de informe')}</InputLabel>
            <Select
              label={t('templates.reportType', 'Tipo de informe')}
              value={categoryFilter}
              onChange={(e) => { setCategoryFilter(e.target.value as string); setPaginationModel((m) => ({ ...m, page: 0 })); }}
            >
              <MenuItem value="">{t('common.all')}</MenuItem>
              {reportTypes.map((rt) => (
                <MenuItem key={rt} value={rt}>{rt}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
        {activeChips}
      </Paper>

      {/* Tabla MUI */}
      <Paper sx={{ p: 1 }} elevation={1}>
        <TableContainer component={Box} sx={{ width: '100%' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('templates.name')}</TableCell>
                <TableCell>{t('templates.description')}</TableCell>
                <TableCell>{t('templates.category')}</TableCell>
                <TableCell>{t('common.status')}</TableCell>
                <TableCell>{t('templates.sizeKb')}</TableCell>
                <TableCell>{t('templates.variables')}</TableCell>
                <TableCell>{t('common.updated')}</TableCell>
                <TableCell align="right">{t('common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading && rows.map((r) => {
                const bytes = (r as any).file_size ?? r.sizeKb ?? 0;
                const kb = Math.round((Number(bytes) || 0) / 1024);
                const canDirect = (r as any)?.settings?.is_direct_upload === true;
                return (
                  <TableRow key={r.id} hover>
                    <TableCell sx={{ maxWidth: 280 }}>
                      <Typography variant="body2" noWrap title={r.name || ''}>{r.name}</Typography>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 360 }}>
                      <Typography variant="body2" noWrap title={r.description || ''}>{r.description}</Typography>
                    </TableCell>
                    <TableCell>{r.category}</TableCell>
                    <TableCell>
                      <Chip size="small" color={(String(r.status).toLowerCase() === 'active') ? 'success' : 'default'} label={(String(r.status).toLowerCase() === 'active') ? t('common.active', 'Activo') : t('common.inactive', 'Inactivo')} />
                    </TableCell>
                    <TableCell>{`${kb} KB`}</TableCell>
                    <TableCell>{formatVariables((r as any).variables_detected || r.variables)}</TableCell>
                    <TableCell>{formatDate((r as any).updated_at || r.updatedAt)}</TableCell>
                    <TableCell align="right">
                      <Button size="small" startIcon={<Visibility />} onClick={() => { loadTemplateDetail(String(r.id)); setDetailOpen(true); }}>
                        {canDirect ? t('templates.viewEdit', 'Ver / Actualizar') : t('templates.viewOnly', 'Ver')}
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
              {!loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center">{t('common.empty', 'Sin datos')}</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <Stack direction="row" justifyContent="flex-end" sx={{ py: 1 }}>
            <Pagination
              page={paginationModel.page + 1}
              onChange={(_, p) => setPaginationModel({ page: p - 1, pageSize: paginationModel.pageSize })}
              count={Math.max(1, Math.ceil(rowCount / paginationModel.pageSize))}
              color="primary"
            />
          </Stack>
        </TableContainer>
      </Paper>

      {/* Modal de detalle */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ paddingTop: '15px' }}>
          {t('templates.detail', 'Detalle de Plantilla')}
          <IconButton
            aria-label="close"
            onClick={() => setDetailOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            ×
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {detailLoading ? (
            <LinearProgress />
          ) : selectedTemplate ? (
            <Stack spacing={2}>
              <TextField label={t('templates.name')} value={selectedTemplate.name} fullWidth disabled />
              <TextField label={t('templates.description')} value={selectedTemplate.description || ''} fullWidth disabled multiline />
              <TextField label={t('templates.category')} value={selectedTemplate.category || ''} fullWidth disabled />
              <TextField label={t('common.status')} value={selectedTemplate.status || ''} fullWidth disabled />
              <TextField label={t('templates.sizeKb')} value={`${selectedTemplate.file_size || 0} KB`} fullWidth disabled />
              <TextField 
                label={t('templates.variables')} 
                value={Array.isArray(selectedTemplate.variables_detected) ? selectedTemplate.variables_detected.join(', ') : ''} 
                fullWidth 
                disabled 
                multiline 
              />
              <TextField label={t('common.created')} value={formatDate(selectedTemplate.created_at)} fullWidth disabled />
              <TextField label={t('common.updated')} value={formatDate(selectedTemplate.updated_at)} fullWidth disabled />
              
              <Divider />
              
              {selectedTemplate?.settings?.is_direct_upload === true ? (
                <>
                  <Typography variant="h6">{t('templates.uploadNew', 'Subir Nueva Versión')}</Typography>
                  <Box
                    onClick={() => fileInputRef.current?.click()}
                    onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
                    onDragEnter={(e) => { e.preventDefault(); setDragActive(true); }}
                    onDragLeave={(e) => { e.preventDefault(); setDragActive(false); }}
                    onDrop={(e) => {
                      e.preventDefault();
                      setDragActive(false);
                      const f = e.dataTransfer.files?.[0];
                      if (f && (f.name.endsWith('.docx') || f.name.endsWith('.doc'))) {
                        setUploadFile(f);
                      }
                    }}
                    sx={{
                      mt: 1,
                      p: 2,
                      border: '2px dashed',
                      borderColor: dragActive ? 'primary.main' : 'divider',
                      borderRadius: 1,
                      textAlign: 'center',
                      cursor: 'pointer',
                      bgcolor: dragActive ? 'action.hover' : 'background.paper',
                    }}
                  >
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      {uploadFile ? uploadFile.name : t('templates.dropHereOrClick', 'Arrastra el archivo aquí o haz clic para seleccionar (.docx, .doc)')}
                    </Typography>
                    {!uploadFile && (
                      <Button variant="outlined" size="small">{t('templates.browse', 'Examinar')}</Button>
                    )}
                  </Box>
                  <input
                    ref={fileInputRef}
                    hidden
                    type="file"
                    accept=".docx,.doc"
                    onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                  />
                </>
              ) : (
                <Alert severity="info">{t('templates.noDirectUpload', 'Esta plantilla no permite subida directa.')}</Alert>
              )}
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailOpen(false)}>{t('common.close')}</Button>
          {selectedTemplate && selectedTemplate?.settings?.is_direct_upload === true && (
            <>
              <Button onClick={downloadTemplate} startIcon={<Download />}>
                {t('templates.download', 'Descargar')}
              </Button>
              <Button 
                onClick={uploadTemplate} 
                startIcon={<Upload />}
                disabled={!uploadFile || uploading}
                variant="contained"
              >
                {uploading ? t('common.uploading', 'Subiendo...') : t('templates.upload', 'Subir')}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TemplatesPage;


